import React, { useState, Fragment, useEffect } from 'react';
import { PDFImageProps, VICustomCoverData, VIReportItem, VIReportSummaryData } from '../../../../utils/models';

import { useAppSelector } from '../../../../store/configureStore';
import { getTutorials } from '../../../../store/tutorials';
import { tutorialConstants } from '../../../../utils/constants';
import { reArrangeVIPhotos } from '../../../../utils/utilities';

import Button from '../../../themed/button';
import Conditional from '../../../common/conditional';
import Elevation from '../../../themed/instruction-modals/elevation';
import Modal from '../../../themed/modal';
import RiskRating from '../riskRating';
import Switch from '../../../themed/switch';
import VisualInspectionReport from '.';
import VIReportSummaryForm from '../../../forms/visual-inspection/viReportSummaryForm';
import VIReportItems from './viReportItems';
import VIPhotoRecordForm from '../../../forms/visual-inspection/viPhotoRecordForm';
import VICustomCoverForm from '../../../forms/visual-inspection/viCustomCoverForm';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

import config from '../../../../utils/config';

const CreateVisualInspectionReport: React.FC = () => {
	const [showAnomalyPage, setShowAnomalyPage] = useState(false);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [viCoverData, setVICoverData] = useState(initialCustomCoverData);
	const [viReportSummary, setVIReportSummary] = useState(initialReportSummary);
	const [viReportItems, setVIReportItems] = useState<VIReportItem[]>([]);
	const [viPhoto, setVIPhoto] = useState<PDFImageProps[]>([]);

	const handleShowAnomalyToggle = (): void => {
		setShowAnomalyPage((previousState) => !previousState);
	};

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const getReportDetails = () => {
		return {
			projectDetails,
			showAnomalyPage,
			viReportItems,
			viReportSummary,
			viCoverData,
			viPhoto,
		};
	};

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setVICoverData(cachedReport.viCoverData);
			setVIReportSummary(cachedReport.viReportSummary);
			setVIReportItems(reArrangeVIPhotos(cachedReport.viReportItems));
			setVIPhoto(cachedReport.viPhoto);
			setShowAnomalyPage(cachedReport.showAnomalyPage);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<VisualInspectionReport {...reportDetails} />
			</Modal>

			<Zippy
				headerTitle="Cover Sheet"
				isValid={viCoverData.coverData.length > 0}
			>
				<VICustomCoverForm onAddCoverForm={setVICoverData} />
			</Zippy>

			<Zippy
				headerTitle="Report Summary"
				isValid={viReportSummary.summary.length > 0}
			>
				<VIReportSummaryForm onProceed={setVIReportSummary} />
			</Zippy>

			<Zippy headerTitle="Inspected Items" isValid={viReportItems.length > 0}>
				<VIReportItems
					reportItems={viReportItems}
					onDataChange={setVIReportItems}
				/>

				<div className="d-flex justify-content-end">
					<Switch
						label="Show Anomaly Breakdown"
						checked={showAnomalyPage}
						onChange={handleShowAnomalyToggle}
					/>
				</div>
			</Zippy>

			<Zippy headerTitle="General Photos" isValid={viPhoto.length > 0}>
				<VIPhotoRecordForm onProceed={setVIPhoto} />
			</Zippy>

			<Conditional isVisible={config.enableRiskRating}>
				<Zippy headerTitle="Risk Rating" isValid={viReportItems.length > 0}>
					<RiskRating data={viReportItems} />
				</Zippy>
			</Conditional>

			<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
				<div className="d-flex justify-content-end">
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</div>
			</Elevation>
		</Fragment>
	);
};

const initialReportSummary: VIReportSummaryData = {
	summary: '',
	remarks: [],
};

const initialCustomCoverData: VICustomCoverData = {
	scopeOfWork: '',
	coverData: [],
};

export default CreateVisualInspectionReport;
